import { GetStaticProps } from 'next';

import { Container, Heading } from '@chakra-ui/react';

import {
  TypeArea,
  TypeAreaSkeleton,
  TypeCity,
  TypeCitySkeleton,
  TypeJobAdvert,
  TypeJobAdvertSkeleton
} from 'generated/contentful';

import { ParsedUrlQuery } from 'querystring';

import { createClient } from 'common/lib/contentful';

import Layout from 'common/components/Layout/Layout';

import JobsBoard from 'features/jobs-board/components/JobsBoard';

export interface AreaJobsBoardParams extends ParsedUrlQuery {
  city: string;
  area: string;
}

interface AreaJobsBoardProps {
  city: TypeCity<'WITHOUT_UNRESOLVABLE_LINKS', string>;
  cities: TypeCity<'WITHOUT_UNRESOLVABLE_LINKS', string>[];
  areas: TypeArea<'WITHOUT_UNRESOLVABLE_LINKS', string>[];
  items: {
    jobAdvert: TypeJobAdvert<'WITHOUT_UNRESOLVABLE_LINKS', string>;
    area: TypeArea<'WITHOUT_UNRESOLVABLE_LINKS', string>;
  }[];
}

export const getStaticPaths = async () => {
  const client = createClient();

  const areas = await client.withoutUnresolvableLinks.getEntries<TypeAreaSkeleton>({
    limit: 1000,
    include: 2
  });

  const paths = areas.items
    .filter(area => area.fields.city)
    .map(area => ({
      params: {
        city: area.fields.city!.fields.slug,
        area: area.fields.slug
      }
    }));

  return {
    paths,
    fallback: false
  };
};

export const getStaticProps: GetStaticProps<AreaJobsBoardProps> = async context => {
  const client = createClient();
  const { city: citySlug, area: areaSlug } = context.params as AreaJobsBoardParams;

  const city = await client.withoutUnresolvableLinks.getEntries<TypeCitySkeleton>({
    content_type: 'city',
    'fields.slug[in]': [citySlug]
  });

  const cities = await client.withoutUnresolvableLinks.getEntries<TypeCitySkeleton>({
    content_type: 'city',
    limit: 100
  });

  const area = await client.withoutUnresolvableLinks.getEntries<TypeAreaSkeleton>({
    content_type: 'area',
    'fields.slug[in]': [areaSlug]
  });

  const areas = await client.withoutUnresolvableLinks.getEntries<TypeAreaSkeleton>({
    content_type: 'area',
    limit: 1000,
    include: 2
  });

  const jobAdverts = await client.withoutUnresolvableLinks.getEntries<TypeJobAdvertSkeleton>({
    content_type: 'jobAdvert',
    limit: 100
  });

  const items = jobAdverts.items.map(jobAdvert => ({
    jobAdvert: jobAdvert as TypeJobAdvert<'WITHOUT_UNRESOLVABLE_LINKS', string>,
    area: area.items[0] as TypeArea<'WITHOUT_UNRESOLVABLE_LINKS', string>
  }));

  return {
    props: {
      city: city.items[0] as TypeCity<'WITHOUT_UNRESOLVABLE_LINKS', string>,
      cities: cities.items as TypeCity<'WITHOUT_UNRESOLVABLE_LINKS', string>[],
      areas: areas.items as TypeArea<'WITHOUT_UNRESOLVABLE_LINKS', string>[],
      area: area.items[0] as TypeArea<'WITHOUT_UNRESOLVABLE_LINKS', string>,
      items
    }
  };
};

export default function AreaJobsBoard({ city, cities, areas, items }: AreaJobsBoardProps) {
  return (
    <Layout
      pageTitle={`Search Cleaning Jobs in ${items[0].area.fields.name}`}
      description={`We have cleaner jobs available in ${items[0].area.fields.name}, so if you’re looking for full-time or part-time cleaning work, search our jobs now!`}
      showStatusBar={false}
      nav="recruit"
    >
      <Container maxW="container.md" py={[12, null, 16]}>
        <Heading size="2xl" mb={4}>
          Search Cleaning Jobs
        </Heading>

        <JobsBoard city={city} cities={cities} area={items[0].area} areas={areas} items={items} />
      </Container>
    </Layout>
  );
}
